import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Lightbulb,
  HandHeart,
  TrendingUp,
  Zap,
  Heart,
  Stethoscope,
  Monitor,
  ArrowRight,
  Clock,
  SlidersHorizontal,
  Car,
  Ban,
  MapPin,
  MessageSquare,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

const pillars = [
  {
    icon: Lightbulb,
    title: "Teknologi og erfaring",
    description:
      "Vi kombinerer moderne teknologi med dyb sundhedsfaglig indsigt. Vores system matcher hurtigt og præcist — men det er erfaringen fra praksis der sikrer kvaliteten.",
  },
  {
    icon: HandHeart,
    title: "Ordentlighed først",
    description:
      "Vi tror på gennemsigtighed, fair vilkår og respekt for alle parter. Det gælder både overfor vikarer og de institutioner vi samarbejder med.",
  },
  {
    icon: TrendingUp,
    title: "Langsigtede relationer",
    description:
      "Vi bygger samarbejder der varer. Faste relationer giver bedre bemanding, gladere medarbejdere og trygge borgere.",
  },
  {
    icon: Zap,
    title: "Hastighed uden kompromis",
    description:
      "Når der opstår et akut behov, reagerer vi med det samme. Men hastighed betyder aldrig at vi går på kompromis med kvaliteten.",
  },
  {
    icon: Heart,
    title: "Mennesker først",
    description:
      "Bag hvert CPR-nummer er en dygtig fagperson. Bag hver institution er borgere der fortjener god pleje. Det glemmer vi aldrig.",
  },
  {
    icon: MessageSquare,
    title: "Feedback fra begge sider",
    description:
      "Vi samler løbende feedback fra både vikar og institution. Det fortæller os hvem der trives hvor, og så sender vi dem derud.",
  },
];

const values = [
  { label: "Stabilitet", description: "Kontinuitet og genkendelighed i plejen" },
  { label: "Ordentlighed", description: "Gennemsigtige vilkår og fair behandling" },
  { label: "Nærvær", description: "Personlige relationer og fast kontaktperson" },
  { label: "Faglighed", description: "Kompetent personale til den rette opgave" },
];

export default function OmOsPage() {
  return (
    <>
      {/* Hero */}
      <section
        className="section relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #e8f9fc 0%, #ffffff 40%, #eef2ff 100%)",
        }}
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-100 rounded-full -translate-y-1/2 translate-x-1/3 opacity-40 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent-100 rounded-full translate-y-1/3 -translate-x-1/4 opacity-30 blur-3xl pointer-events-none" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20 md:py-24">
          <motion.div
            initial="hidden"
            animate="visible"
            transition={{ staggerChildren: 0.1 }}
            className="max-w-3xl"
          >
            <motion.div variants={fadeUp} transition={{ duration: 0.5 }}>
              <span className="kicker">Om Plejesøstrene</span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              transition={{ duration: 0.5 }}
              className="mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight tracking-tight text-slate-900"
            >
              Sundhedsfaglig erfaring{" "}
              <span className="text-brand-500 subtle-glow">
                møder moderne teknologi
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.5 }}
              className="mt-5 text-slate-500 text-base sm:text-lg max-w-2xl leading-relaxed"
            >
              Vi er et vikarbureau med rod i plejesektoren. Stiftet af mennesker
              der kender hverdagen på gulvet — og ved hvordan teknologi kan gøre
              den bedre.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Vores historie */}
      <section className="section py-10 sm:py-14 md:py-18 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="kicker">Vores historie</span>
            <h2 className="section-title mt-2">
              Derfor hedder vi Plejesøstrene
            </h2>

            <div className="mt-8 space-y-5 text-slate-600 leading-relaxed">
              <p>
                Navnet <strong className="text-slate-900">Plejesøstrene</strong>{" "}
                er inspireret af det bosniske ord{" "}
                <em className="text-brand-600">"sestra"</em>, der betyder både{" "}
                <strong className="text-slate-900">søster</strong> og{" "}
                <strong className="text-slate-900">sygeplejerske</strong>. Det
                afspejler præcis det, vi bygger vores arbejde på: nærvær, varme
                og en dyb forståelse for den rolle sundhedspersonalet spiller i
                menneskers liv.
              </p>
              <p>
                Plejesøstrene blev grundlagt med en enkel overbevisning:{" "}
                <strong className="text-slate-900">
                  vikarbranchen kan gøres bedre
                </strong>
                . Bedre for de fagpersoner der tager vagterne. Bedre for de
                institutioner der har brug for hjælp. Og bedre for de borgere
                der fortjener stabil og omsorgsfuld pleje.
              </p>
              <p>
                Vi så et marked præget af langsomme processer, upersonlige
                bureauer og mangelfuld matching. Vi vidste det kunne gøres
                anderledes — med den rette kombination af sundhedsfaglig
                forståelse og intelligent teknologi.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stifterne */}
      <section className="section py-10 sm:py-14 md:py-18 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <span className="kicker">Folkene bag</span>
            <h2 className="section-title mt-2">
              Grundlagt af erfaring og ambition
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div className="card-elevated p-6">
              <div className="w-12 h-12 rounded-xl bg-brand-50 flex items-center justify-center mb-4">
                <Stethoscope className="text-brand-600" size={24} />
              </div>
              <h3 className="font-semibold text-slate-900 text-lg">
                Sundhedsfaglig erfaring
              </h3>
              <p className="mt-3 text-sm text-slate-500 leading-relaxed">
                Med mange års erfaring fra den kommunale plejesektor —
                plejehjem, hjemmepleje og bosteder — kender Camilla
                hverdagens udfordringer indefra. Den erfaring sikrer, at vi
                altid matcher de rette kompetencer til de rette opgaver.
              </p>
            </div>

            <div className="card-elevated p-6">
              <div className="w-12 h-12 rounded-xl bg-accent-50 flex items-center justify-center mb-4">
                <Monitor className="text-accent-600" size={24} />
              </div>
              <h3 className="font-semibold text-slate-900 text-lg">
                Teknologisk ekspertise
              </h3>
              <p className="mt-3 text-sm text-slate-500 leading-relaxed">
                Med baggrund i IT og processoptimering har Osman
                bygget de systemer der gør Plejesøstrene hurtige, præcise og
                pålidelige. Teknologien er værktøjet — men det er den
                sundhedsfaglige indsigt der driver retningen.
              </p>
            </div>
          </div>

          <p className="mt-8 text-slate-600 leading-relaxed max-w-3xl">
            Sammen forener vi to verdener der sjældent mødes:{" "}
            <strong className="text-slate-900">
              den praktiske virkelighed i plejesektoren
            </strong>{" "}
            og{" "}
            <strong className="text-slate-900">
              moderne teknologis muligheder
            </strong>
            . Det er den kombination der gør Plejesøstrene unikt.
          </p>
        </div>
      </section>

      {/* Intelligent matching */}
      <section className="section py-10 sm:py-14 md:py-18 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="kicker">Intelligent matching</span>
            <h2 className="section-title mt-2">
              Den rette vikar til den rette vagt — hver gang
            </h2>
            <p className="section-subtitle">
              Vores system tager højde for langt mere end bare "hvem er
              ledig?". Hvert match vurderes på tværs af flere parametre, så
              resultatet er præcist — ikke tilfældigt.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="card-elevated p-6">
              <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center mb-4">
                <Stethoscope className="text-brand-600" size={20} />
              </div>
              <h3 className="font-semibold text-slate-900 text-base">
                Faglige kompetencer
              </h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                Medicinhåndtering, sårpleje, kateter, stomi, diabetes, demens,
                palliation — vi matcher på konkrete kvalifikationer, ikke bare
                faggruppe.
              </p>
            </div>

            <div className="card-elevated p-6">
              <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center mb-4">
                <Clock className="text-brand-600" size={20} />
              </div>
              <h3 className="font-semibold text-slate-900 text-base">
                Tilgængelighed i realtid
              </h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                Vi kender vikarens aktuelle status live. Ingen gætværk — vi ved
                præcis hvem der reelt er tilgængelig lige nu.
              </p>
            </div>

            <div className="card-elevated p-6">
              <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center mb-4">
                <SlidersHorizontal className="text-brand-600" size={20} />
              </div>
              <h3 className="font-semibold text-slate-900 text-base">
                Personlige præferencer
              </h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                Foretrækker vikaren plejehjem eller hjemmepleje? Dag- eller
                aftenvagter? Systemet respekterer individuelle ønsker
                automatisk.
              </p>
            </div>

            <div className="card-elevated p-6">
              <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center mb-4">
                <Car className="text-brand-600" size={20} />
              </div>
              <h3 className="font-semibold text-slate-900 text-base">
                Transport
              </h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                Kræver vagten bil? Er vikaren på cykel? Vi sikrer at
                transportmuligheder matcher — så vikaren reelt kan komme frem.
              </p>
            </div>

            <div className="card-elevated p-6">
              <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center mb-4">
                <Ban className="text-brand-600" size={20} />
              </div>
              <h3 className="font-semibold text-slate-900 text-base">
                Blokering begge veje
              </h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                Har vikaren eller institutionen haft dårlige erfaringer?
                Systemet husker det og undgår automatisk dårlige match — ingen
                manuelle lister.
              </p>
            </div>

            <div className="card-elevated p-6">
              <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center mb-4">
                <MapPin className="text-brand-600" size={20} />
              </div>
              <h3 className="font-semibold text-slate-900 text-base">
                Institutionstype
              </h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                Plejehjem, hjemmepleje og bosteder stiller forskellige krav. Vi
                matcher vikarer med erfaring fra den rette type institution.
              </p>
            </div>
          </div>

          <p className="mt-10 text-slate-600 leading-relaxed max-w-3xl">
            Resultatet er et match der ikke bare er "den nærmeste ledige" — men
            den{" "}
            <strong className="text-slate-900">
              bedste kombination af kompetencer, præferencer og praktiske
              forhold
            </strong>
            . Det hele sker på sekunder.
          </p>
        </div>
      </section>

      {/* Vores tilgang */}
      <section className="section py-10 sm:py-14 md:py-18 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="kicker">Vores tilgang</span>
            <h2 className="section-title mt-2">Det vi tror på</h2>
            <p className="section-subtitle mx-auto">
              Vi er ikke bare et vikarbureau. Vi er en samarbejdspartner der
              investerer i langsigtede relationer og ordentlige løsninger.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {pillars.map((p, i) => (
              <div
                key={p.title}
                className={`card-elevated p-6 text-center ${
                  i === pillars.length - 1 && pillars.length % 3 !== 0
                    ? "sm:col-span-2 lg:col-span-1"
                    : ""
                }`}
              >
                <div className="w-12 h-12 rounded-xl bg-brand-50 flex items-center justify-center mx-auto mb-4">
                  <p.icon className="text-brand-600" size={24} />
                </div>
                <h3 className="font-semibold text-slate-900 text-base">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                  {p.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Værdier */}
      <section className="section py-10 sm:py-14 md:py-18 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="kicker">Vores værdier</span>
            <h2 className="section-title mt-2">Det vi står for</h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v) => (
              <div
                key={v.label}
                className="text-center p-6 rounded-card border border-slate-100"
              >
                <h3 className="text-lg font-bold text-brand-600">{v.label}</h3>
                <p className="mt-2 text-sm text-slate-500">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="section relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #e8f9fc 0%, #ffffff 40%, #eef2ff 100%)",
        }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
            Vil du være en del af Plejesøstrene?
          </h2>
          <p className="mt-4 text-slate-500 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
            Uanset om du er vikar eller institution — vi er klar til en snak.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/for-vikarer" className="btn btn-primary btn-lg">
              Bliv vikar
              <ArrowRight size={18} />
            </Link>
            <Link to="/for-institutioner" className="btn btn-secondary btn-lg">
              For institutioner
            </Link>
            <Link to="/kontakt" className="btn btn-ghost btn-lg">
              Kontakt os
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
